// src/pages/Member/CreateMember/CreateMemberRelation/CreateMemberRelation.jsx
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";

import api from "@/utils/axios";
import i18n from "@/i18n";
import { useAuth } from "@/app/auth-context-state";
import cls from "./CreateMemberRelation.module.scss";

/* Enums */
import { GENDERS } from "@/enums/MemberEnums";
import {
  RELATIONSHIP_TYPES,
  RELATIONSHIP_STATUSES,
} from "@/enums/PartnerEnums";

/* Art */
import mrImg from "@/assets/UI/memberCreate/q5Mr.webp";
import mrMImg from "@/assets/UI/memberCreate/q5MrM.webp";
import mrsImg from "@/assets/UI/memberCreate/q5Mrs.webp";
import mrsMImg from "@/assets/UI/memberCreate/q5MrsM.webp";

/* ---- Framer animations (same vibe as Local) ---- */
const floatSlow = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.55, 0.72, 0.55],
    scale: [1, 1.03, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const isAtLeast18 = (dateStr) => {
  if (!dateStr) return false;
  const birthday = new Date(dateStr);
  if (Number.isNaN(birthday.getTime())) return false;
  const now = new Date();
  const cutoff = new Date(
    now.getFullYear() - 18,
    now.getMonth(),
    now.getDate(),
  );
  return birthday <= cutoff;
};

const floatSlowAlt = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.72, 0.58, 0.72],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

export default function CreateMemberRelation() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();

  const [submitting, setSubmitting] = useState(false);

  // "single" | "couple"
  const [memberType, setMemberType] = useState("single");

  // Couple-only
  const [relationshipType, setRelationshipType] = useState("");
  const [relationshipStatus, setRelationshipStatus] = useState("");
  const [birthday, setBirthday] = useState("");
  const [gender, setGender] = useState("");

  const t = (k, o) => i18n.t(k, o);

  const MrD = useMemo(() => mrImg, []);
  const MrsD = useMemo(() => mrsImg, []);
  const MrM = useMemo(() => mrMImg, []);
  const MrsM = useMemo(() => mrsMImg, []);

  const title = t("onboarding.relation.title", { defaultValue: "" });
  const p1 = t("onboarding.relation.p1", { defaultValue: "" });
  const p2 = t("onboarding.relation.p2", { defaultValue: "" });
  const p3 = t("onboarding.relation.p3", { defaultValue: "" });

  const memberTypeQ = t("onboarding.relation.memberType.q", {
    defaultValue: "Pick what describes you best",
  });
  const singleLabel = t("onboarding.relation.memberType.single", {
    defaultValue: "Single",
  });
  const coupleLabel = t("onboarding.relation.memberType.couple", {
    defaultValue: "Couple",
  });

  const relationshipTypeLabel = t(
    "onboarding.relation.relationshipType.label",
    {
      defaultValue: "Relationship type",
    },
  );
  const relationshipTypeHint = t("onboarding.relation.relationshipType.hint", {
    defaultValue: "",
  });

  const relationshipStatusLabel = t(
    "onboarding.relation.relationshipStatus.label",
    { defaultValue: "Relationship status" },
  );
  const relationshipStatusHint = t(
    "onboarding.relation.relationshipStatus.hint",
    { defaultValue: "" },
  );

  const partnerBirthdayLabel = t("onboarding.relation.partnerBirthday.label", {
    defaultValue: "Partner birthday",
  });
  const partnerBirthdayHint = t("onboarding.relation.partnerBirthday.hint", {
    defaultValue: "",
  });

  const partnerGenderLabel = t("onboarding.relation.partnerGender.label", {
    defaultValue: "Partner gender",
  });
  const partnerGenderHint = t("onboarding.relation.partnerGender.hint", {
    defaultValue: "",
  });

  const noteCopy = t("onboarding.relation.note", { defaultValue: "" });
  const closer = t("onboarding.relation.closer", { defaultValue: "" });

  const cta = t("common.continue", { defaultValue: "Continue" });
  const savingLabel = t("common.saving", { defaultValue: "Saving..." });

  const successSingle = t("onboarding.relation.success.single", {
    defaultValue: "Saved!",
  });
  const successCouple = t("onboarding.relation.success.couple", {
    defaultValue: "Saved!",
  });
  const errorMsg = t("onboarding.relation.error", {
    defaultValue: "We couldn’t save this right now. Please try again.",
  });

  const isCouple = memberType === "couple";
  const canSubmit =
    !isCouple ||
    (relationshipType &&
      relationshipStatus &&
      birthday &&
      gender &&
      isAtLeast18(birthday));

  const memberTypeOptions = [
    {
      value: "single",
      emoji: "🧍",
      title: singleLabel,
      hint: t("onboarding.relation.memberType.singleHint", {
        defaultValue: "Just you. Simple and easy.",
      }),
    },
    {
      value: "couple",
      emoji: "💑",
      title: coupleLabel,
      hint: t("onboarding.relation.memberType.coupleHint", {
        defaultValue: "You and your partner. Let’s add a few details.",
      }),
    },
  ];

  const relationshipTypeOptions = RELATIONSHIP_TYPES.map((value) => ({
    value,
    label: t(`enums.relationshipTypes.${value}`),
  }));

  const relationshipStatusOptions = RELATIONSHIP_STATUSES.map((value) => ({
    value,
    label: t(`enums.relationshipStatuses.${value}`),
  }));

  const genderOptions = GENDERS.map((value) => ({
    value,
    label: t(`enums.gender.${value}`),
  }));

  async function onSubmit(e) {
    e.preventDefault();

    if (isCouple && !canSubmit) {
      toast.info(
        t("onboarding.relation.errors.missingFields", {
          defaultValue: "Please complete all couple details to continue.",
        }),
      );
      return;
    }

    if (isCouple && !isAtLeast18(birthday)) {
      toast.info(
        t("onboarding.relation.errors.partnerMustBe18", {
          defaultValue: "Partner must be at least 18 years old.",
        }),
      );
      return;
    }

    const payload =
      memberType === "single"
        ? { memberType: "single" }
        : {
            memberType: "couple",
            relationshipType,
            relationshipStatus,
            birthday,
            gender,
          };

    setSubmitting(true);
    try {
      const { data } = await api.post("/v1/member-create/relation", payload, {
        skipToast: true,
      });

      // Keep auth-driven layout context in sync before the next onboarding page.
      await refreshMe();

      toast.success(isCouple ? successCouple : successSingle);
      navigate(data?.next || "/member/sexual_identity", { replace: true });
    } catch (err) {
      console.error("save relation failed:", err);
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className={cls.page}>
      {/* Fixed background stage */}
      <section className={cls.heroStage} aria-hidden="true">
        <div className={cls.mobileOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrM}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsM}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrD}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsD}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.stageTint} />
      </section>

      {/* Content layer */}
      <div className={cls.pageInner}>
        <div className={cls.contentShell}>
          <div className={cls.formCard}>
            <div className={cls.formTop}>
              <div className={cls.formMeta}>
                <div className={cls.formTitle}>{title}</div>

                {(p1 || p2 || p3) && (
                  <div className={cls.formSub}>
                    {[p1, p2, p3].filter(Boolean).map((line, idx) => (
                      <div key={idx}>{line}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <form className={cls.form} onSubmit={onSubmit} noValidate>
              {/* Member type (same option UI as Local) */}
              <div className={cls.block}>
                <div className={cls.blockText}>
                  <p className={cls.blockP}>{memberTypeQ}</p>
                </div>

                <fieldset className={cls.options}>
                  <legend className={cls.srOnly}>{memberTypeQ}</legend>

                  {memberTypeOptions.map((opt) => {
                    const checked = memberType === opt.value;
                    return (
                      <label
                        key={opt.value}
                        className={`${cls.option} ${
                          checked ? cls.optionActive : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="memberType"
                          value={opt.value}
                          checked={checked}
                          onChange={() => setMemberType(opt.value)}
                        />

                        <span className={cls.optionBody}>
                          <span className={cls.optionEmoji} aria-hidden="true">
                            {opt.emoji}
                          </span>

                          <span className={cls.optionText}>
                            <strong>{opt.title}</strong>
                            <small>{opt.hint}</small>
                          </span>

                          <span className={cls.optionDot} aria-hidden="true" />
                        </span>
                      </label>
                    );
                  })}
                </fieldset>
              </div>

              {/* Couple-only fields */}
              {isCouple && (
                <div className={cls.block}>
                  <div className={cls.grid2}>
                    {/* relationshipType */}
                    <label className={cls.field}>
                      <span className={cls.fieldTop}>
                        <span className={cls.fieldLabel}>
                          {relationshipTypeLabel}
                        </span>
                        {relationshipTypeHint && (
                          <small className={cls.fieldHint}>
                            {relationshipTypeHint}
                          </small>
                        )}
                      </span>

                      <div className={cls.selectWrap}>
                        <select
                          className={cls.select}
                          value={relationshipType}
                          onChange={(e) => setRelationshipType(e.target.value)}
                          aria-label={relationshipTypeLabel}
                        >
                          <option value="">
                            {t("common.pickOne", { defaultValue: "Pick one…" })}
                          </option>
                          {relationshipTypeOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>

                        <span className={cls.selectChevron} aria-hidden="true">
                          ▾
                        </span>
                        <span className={cls.selectGlow} aria-hidden="true" />
                      </div>
                    </label>

                    {/* relationshipStatus */}
                    <label className={cls.field}>
                      <span className={cls.fieldTop}>
                        <span className={cls.fieldLabel}>
                          {relationshipStatusLabel}
                        </span>
                        {relationshipStatusHint && (
                          <small className={cls.fieldHint}>
                            {relationshipStatusHint}
                          </small>
                        )}
                      </span>

                      <div className={cls.selectWrap}>
                        <select
                          className={cls.select}
                          value={relationshipStatus}
                          onChange={(e) =>
                            setRelationshipStatus(e.target.value)
                          }
                          aria-label={relationshipStatusLabel}
                        >
                          <option value="">
                            {t("common.pickOne", { defaultValue: "Pick one…" })}
                          </option>
                          {relationshipStatusOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>

                        <span className={cls.selectChevron} aria-hidden="true">
                          ▾
                        </span>
                        <span className={cls.selectGlow} aria-hidden="true" />
                      </div>
                    </label>
                  </div>

                  <div className={cls.grid2}>
                    {/* birthday */}
                    <label className={cls.field}>
                      <span className={cls.fieldTop}>
                        <span className={cls.fieldLabel}>
                          {partnerBirthdayLabel}
                        </span>
                        {partnerBirthdayHint && (
                          <small className={cls.fieldHint}>
                            {partnerBirthdayHint}
                          </small>
                        )}
                      </span>

                      <input
                        className={cls.input}
                        type="date"
                        value={birthday}
                        onChange={(e) => setBirthday(e.target.value)}
                        aria-label={partnerBirthdayLabel}
                      />
                    </label>

                    {/* gender */}
                    <label className={cls.field}>
                      <span className={cls.fieldTop}>
                        <span className={cls.fieldLabel}>
                          {partnerGenderLabel}
                        </span>
                        {partnerGenderHint && (
                          <small className={cls.fieldHint}>
                            {partnerGenderHint}
                          </small>
                        )}
                      </span>

                      <div className={cls.selectWrap}>
                        <select
                          className={cls.select}
                          value={gender}
                          onChange={(e) => setGender(e.target.value)}
                          aria-label={partnerGenderLabel}
                        >
                          <option value="">
                            {t("common.pickOne", { defaultValue: "Pick one…" })}
                          </option>
                          {genderOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>

                        <span className={cls.selectChevron} aria-hidden="true">
                          ▾
                        </span>
                        <span className={cls.selectGlow} aria-hidden="true" />
                      </div>
                    </label>
                  </div>

                  {noteCopy && <div className={cls.note}>{noteCopy}</div>}
                  {closer && <div className={cls.closer}>{closer}</div>}
                </div>
              )}

              {/* If single, still show note/closer if you want */}
              {!isCouple && (
                <>
                  {noteCopy && <div className={cls.note}>{noteCopy}</div>}
                  {closer && <div className={cls.closer}>{closer}</div>}
                </>
              )}

              {/* Sticky submit like Local */}
              <div className={cls.actions}>
                <button
                  type="submit"
                  className={cls.submit}
                  disabled={submitting || !canSubmit}
                  aria-busy={submitting ? "true" : "false"}
                >
                  {submitting ? savingLabel : cta}
                </button>
              </div>
            </form>
          </div>

          <div className={cls.bottomSpace} />
        </div>
      </div>
    </main>
  );
}
